import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { fetchDeviceLogEntries } from '../../apis/deviceLogsApi';
import { IDeviceLog, IDeviceLogEntry } from '../../interface/DeviceLog';

const PAGE_SIZE = 20;

interface DeviceLoginHistoryModalProps {
    // The Device Logs row that was clicked (one person on one device)
    log: IDeviceLog;
    // Same range as the list, so the popup adds up to the row's count
    fromDate: string;
    toDate: string;
    onClose: () => void;
}

// Every login of one person from one device in the selected date range
const DeviceLoginHistoryModal: React.FC<DeviceLoginHistoryModalProps> = ({ log, fromDate, toDate, onClose }) => {
    const [entries, setEntries] = useState<IDeviceLogEntry[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

    useEffect(() => {
        if (log.employeePk == null) {
            setError('This login has no employee linked to it.');
            setIsLoading(false);
            return;
        }
        const controller = new AbortController();
        setIsLoading(true);
        setError(null);
        fetchDeviceLogEntries(log.employeePk, log.deviceUuid, fromDate, toDate, page, PAGE_SIZE, controller.signal)
            .then((result) => {
                setEntries(result.data);
                setTotalItems(result.totalItems);
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                setError(err instanceof Error ? err.message : 'Failed to load logins');
                setEntries([]);
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });
        // Page changed or popup closed before the answer came back
        return () => controller.abort();
    }, [log.employeePk, log.deviceUuid, fromDate, toDate, page]);

    // Dark backdrop (App.css draws it for body.modal-open) and close on Escape
    useEffect(() => {
        document.body.classList.add('modal-open');
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => {
            document.body.classList.remove('modal-open');
            window.removeEventListener('keydown', handleKey);
        };
    }, [onClose]);

    // Stored in UTC, show it in the viewer's local time
    const local = (value: string) => moment.utc(value).local();

    const device = [log.platform, log.deviceName].filter(Boolean).join(' · ') || '-';
    const period = `${moment(fromDate).format('MM-DD-YYYY')} to ${moment(toDate).format('MM-DD-YYYY')}`;
    const firstShown = (page - 1) * PAGE_SIZE + 1;
    const lastShown = Math.min(page * PAGE_SIZE, totalItems);

    return (
        <div
            className="modal fade show editAsignment deviceLoginModal overflow-auto align-items-md-center align-items-start justify-content-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deviceLoginModalTitle"
            // Clicking outside the dialog closes it
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="modal-dialog w-100">
                <div className="modal-content p-4">
                    <div className="modal-body p-lg-2 p-md-2 p-0">
                        <div className="formCard mt-0 p-0 border-0">
                            <div className="FormTitleWithdivider position-relative d-flex align-items-center gap-3 mb-3">
                                <h3 id="deviceLoginModalTitle" className="text-nowrap formTitle m-0">
                                    Login History
                                </h3>
                                <hr className="w-100" />
                                <button type="button" className="deviceLoginClose" aria-label="Close" onClick={onClose}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                        <path d="M19 5.5L5 19.5M19 19.5L5 5.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            <dl className="deviceLoginSummary">
                                <div>
                                    <dt>User</dt>
                                    <dd>
                                        {log.userName || '-'}
                                        {log.personalId && <span className="deviceLoginMuted"> ({log.personalId})</span>}
                                    </dd>
                                </div>
                                <div>
                                    <dt>Device</dt>
                                    <dd>{device}</dd>
                                </div>
                                <div className="deviceLoginSummaryWide">
                                    <dt>Device ID</dt>
                                    <dd className="deviceLoginUuid">{log.deviceUuid || 'Not sent by the app'}</dd>
                                </div>
                                <div>
                                    <dt>Period</dt>
                                    <dd>{period}</dd>
                                </div>
                                <div>
                                    <dt>Total logins</dt>
                                    <dd>{isLoading && totalItems === 0 ? log.loginCount : totalItems}</dd>
                                </div>
                            </dl>

                            <div className="deviceLoginList">
                                <table className="table mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="text-center py-4">
                                                    {isLoading ? 'Loading...' : error || 'No logins found.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            entries.map((entry, index) => (
                                                <tr key={entry.id} className={isLoading ? 'opacity-50' : ''}>
                                                    {/* Numbered oldest = 1, so the newest has the highest number */}
                                                    <td>{totalItems - (firstShown - 1) - index}</td>
                                                    <td>{local(entry.createdAt).format('MM-DD-YYYY')}</td>
                                                    <td>{local(entry.createdAt).format('hh:mm:ss A')}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="deviceLoginPager d-flex align-items-center justify-content-between gap-2 mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-transparent"
                                        disabled={page <= 1 || isLoading}
                                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                    >
                                        ‹ Newer
                                    </button>
                                    <span>
                                        {firstShown}–{lastShown} of {totalItems}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-transparent"
                                        disabled={page >= totalPages || isLoading}
                                        onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                    >
                                        Older ›
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeviceLoginHistoryModal;
